import React, { useState } from "react";
import moment from "moment/moment";
import SearchInput from "./SearchInput";
import ArrowDownIcon from "../../assets/icons/arrowDown";
import PrevIcon from "../../assets/icons/prev";
import NextIcon from "../../assets/icons/next";
import { isAdmin, hasPermission } from "../../Utils/auth";

const HolidayTable = ({ data, handleEdit, handleDelete }) => {
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  // Filter logic
  const filteredData = data?.filter((row) => {
    return row.title.toLowerCase().includes(search.toLowerCase());
  });

  const totalPages = Math.max(1, Math.ceil(filteredData?.length / rowsPerPage));

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const currentRows = filteredData?.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  return (
    <div className="w-full rounded-lg">
      {/* Filters */}
      <div className="flex justify-between items-start md:flex-row gap-3 mb-4">
        <SearchInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by title..."
          fontSize="sm"
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="table-auto w-full text-sm">
          <thead>
            <tr className="h-[52px] bg-white border border-l-0 border-r-0 border-[#a48572]/10 text-[#142b70]">
              <th className="px-4 text-left text-sm font-semibold">S No.</th>
              <th className="px-4 text-left text-sm font-semibold">Title</th>
              <th className="px-4 text-center text-sm font-semibold">Date</th>
              <th className="px-4 text-center text-sm font-semibold">
                Location
              </th>
              <th className="px-4 text-center text-sm font-semibold">
                Last Modified Date
              </th>
              <th className="px-4 text-center text-sm font-semibold">Action</th>
            </tr>
          </thead>
          <tbody>
            {currentRows?.length > 0 ? (
              currentRows.map((row, idx) => (
                <tr
                  key={idx}
                  className={`relative "text-[#142b70]/80 hover:bg-[#f9f9f9]`}
                >
                  <td className="relative px-4 text-left py-3 pl-3">
                    <p className="text-sm">
                      {(currentPage - 1) * rowsPerPage + idx + 1}
                    </p>
                  </td>
                  <td className="px-4 text-left py-3">{row.title}</td>
                  <td className="px-4 text-center py-3">{row.holiday_date}</td>
                  <td className="px-4 text-center py-3">
                    {row.location?.name}
                  </td>
                  <td className="text-sm text-center py-3">
                    {row.updated_at
                      ? moment(new Date(row.updated_at)).format(
                          "DD MMM YYYY,  hh:mm A"
                        )
                      : "N/A"}
                  </td>
                  <td className="text-sm text-center py-3">
                    <div className="flex justify-center gap-2">
                      {isAdmin() ? (
                        <>
                          <div className="flex justify-center gap-2">
                            <div
                              className={`flex justify-center items-center w-[90px] px-2 py-1 rounded bg-[#142b70]/10 cursor-pointer`}
                              onClick={() => handleEdit(row)}
                            >
                              <p className={`text-xs text-[#142b70]`}>Edit</p>
                            </div>
                          </div>
                          <div className="flex justify-center gap-2">
                            <div
                              className={`flex justify-center items-center w-[90px] px-2 py-1 rounded bg-[#de2b26]/10 cursor-pointer`}
                              onClick={() => handleDelete(row)}
                            >
                              <p className={`text-xs text-[#142b70]`}>Delete</p>
                            </div>
                          </div>
                        </>
                      ) : !isAdmin() && hasPermission("edit_holidays") ? (
                        <div className="flex justify-center gap-2">
                          <div
                            className={`flex justify-center items-center w-[90px] px-2 py-1 rounded bg-[#de2b26]/10 cursor-pointer`}
                            onClick={() => handleEdit(row)}
                          >
                            <p className={`text-xs text-[#142b70]`}>Edit</p>
                          </div>
                        </div>
                      ) : !isAdmin() && hasPermission("delete_holidays") ? (
                        <div className="flex justify-center gap-2">
                          <div
                            className={`flex justify-center items-center w-[90px] px-2 py-1 rounded bg-[#de2b26]/10 cursor-pointer`}
                            onClick={() => handleDelete(row)}
                          >
                            <p className={`text-xs text-[#142b70]`}>Delete</p>
                          </div>
                        </div>
                      ) : (
                        "Not Allowed"
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="text-center py-4">
                  No results found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-4">
        <div className="relative w-[190px] h-[30px] flex items-center justify-between">
          {/* Page Selector */}
          <div className="flex items-center gap-2">
            <div className="flex items-center h-[26px] px-[10px] rounded-lg bg-[#f9f9f9] cursor-pointer relative">
              <select
                value={currentPage}
                onChange={(e) => handlePageChange(Number(e.target.value))}
                className="bg-transparent text-xs text-[#142b70]/80 focus:outline-none cursor-pointer appearance-none pr-5"
              >
                {Array.from({ length: totalPages }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </select>
              {/* Down Arrow */}
              <ArrowDownIcon
                iconClass="absolute right-2 pointer-events-none"
                color="#142b70"
              />
            </div>
            <p className="text-sm text-[#142b70]/80">of {totalPages} pages</p>
          </div>

          {/* Prev / Next Arrows */}
          <div className="flex items-center gap-3">
            {/* Prev */}
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="disabled:opacity-40"
            >
              <PrevIcon color="#142b70" />
            </button>

            {/* Next */}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="disabled:opacity-40"
            >
              <NextIcon color="#142b70" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HolidayTable;
